import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '../Button';
import { Hint } from '../Hint';
import { Text } from '../Text';

import { Dialog, IDialogProps } from './Dialog';

interface IConfirmDialogProps extends Omit<IDialogProps, 'footer' | 'onClose'> {
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  confirmDisabled?: boolean;
  tone?: 'accent' | 'danger';
}

const ConfirmDialog: FC<IConfirmDialogProps> = ({
  onConfirm,
  onClose,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  tone = 'danger',
  busy = false,
  children,
  ...props
}) => {
  const intl = useIntl();
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  useEffect(() => {
    if (!props.isOpen) setError('');
  }, [props.isOpen]);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  // The caller owns success/close state; rejected operations leave this dialog open.
  const confirm = async (): Promise<void> => {
    if (pendingRef.current || busy || confirmDisabled) return;
    pendingRef.current = true;
    setPending(true);
    setError('');
    try {
      await onConfirm();
    } catch {
      if (mounted.current)
        setError(intl.formatMessage({ id: 'server-error.went-wrong' }));
    } finally {
      pendingRef.current = false;
      if (mounted.current) setPending(false);
    }
  };
  return (
    <Dialog
      {...props}
      onClose={onClose}
      busy={busy || pending}
      footer={
        <>
          <Button
            mode="tertiary"
            color="dark"
            disabled={busy || pending}
            onClick={onClose}
          >
            {cancelLabel ?? <FormattedMessage id="common.cancel" />}
          </Button>
          <Button
            mode={tone === 'danger' ? 'secondary' : 'primary'}
            color={tone}
            disabled={confirmDisabled || busy || pending}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {error && (
        <Hint mode="danger">
          <Text variant="bodySmall" color="inherit">
            {error}
          </Text>
        </Hint>
      )}
    </Dialog>
  );
};

export { ConfirmDialog };
